// Role-based route protection component
import { Button, Result } from 'antd';
import React, { useCallback } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { AuthBackground } from '@/components/common';
import { ROUTES } from '@/constants';
import { getAccessibleRoutes } from '@/constants/menu-permissions';
import { isAdminPanelRole, normalizeUserRole } from '@/constants/user-roles';
import { useCurrentUser } from '@/hooks/auth';
import { UserRole } from '@/types';
import { useTranslation } from 'react-i18next';

interface RoleProtectedRouteProps {
	children: React.ReactNode;
	requiredRole?: UserRole;
	fallbackRoute?: string;
}

const RoleProtectedRoute = ({
	children,
	requiredRole,
	fallbackRoute = ROUTES.DASHBOARD,
}: RoleProtectedRouteProps) => {
	const { t } = useTranslation('components');
	const location = useLocation();
	const navigate = useNavigate();
	const { data: user, isLoading, error } = useCurrentUser();

	const handleGoToDashboard = useCallback(() => {
		navigate(ROUTES.DASHBOARD);
	}, [navigate]);

	const handleGoToFallback = useCallback(() => {
		navigate(fallbackRoute);
	}, [navigate, fallbackRoute]);

	// Show loading while checking authentication
	if (isLoading) {
		return (
			<AuthBackground variant='default'>
				<div className='text-center'>
					<div className='text-white text-lg'>{t('auth.validatingAccess')}</div>
				</div>
			</AuthBackground>
		);
	}

	// Redirect unauthenticated users to login
	if (error || !user) {
		return <Navigate to={ROUTES.LOGIN} state={{ from: location }} replace />;
	}

	const userRole = normalizeUserRole(user.role);

	if (!isAdminPanelRole(userRole)) {
		return (
			<AuthBackground variant='minimal'>
				<div className='bg-white rounded-2xl shadow-xl p-8 max-w-md w-full border border-gray-100'>
					<Result
						status='403'
						title={<span className='text-2xl font-bold text-gray-900'>{t('auth.accessDenied')}</span>}
						subTitle={
							<span className='text-gray-500'>
								This panel is for platform staff only. Pet owners use the mobile app.
							</span>
						}
					/>
				</div>
			</AuthBackground>
		);
	}

	const accessibleRoutes = getAccessibleRoutes(userRole);
	const currentPath = location.pathname;

	// Check if user has access to current route
	// Handle dynamic routes by checking if current path starts with any accessible route pattern
	const hasAccess = accessibleRoutes.some(route =>
		route.endsWith('/') ? currentPath.startsWith(route) : currentPath === route
	);

	// Check specific role requirement if specified
	// clinic_admin should not have access to admin-only routes
	if (requiredRole && userRole !== requiredRole) {
		return (
			<AuthBackground variant='minimal'>
				<div className='bg-white rounded-2xl shadow-xl max-w-md w-full border border-gray-100'>
					<Result
						status='403'
						title={<span className='text-2xl font-bold text-gray-900'>{t('auth.accessDenied')}</span>}
						subTitle={<span className='text-gray-500'>{t('auth.roleRequired', { role: requiredRole, userRole })}</span>}
						extra={
							<div className='flex flex-col gap-3 mt-4'>
								<Button type='primary' size='large' block onClick={handleGoToDashboard} className='bg-indigo-600 hover:bg-indigo-700 h-10 rounded-lg'>
									{t('auth.goToDashboard')}
								</Button>
								<Button size='large' block onClick={handleGoToFallback} className='h-10 rounded-lg border-gray-300 hover:border-gray-400 text-gray-700'>
									{t('auth.goToFallback')}
								</Button>
							</div>
						}
					/>
				</div>
			</AuthBackground>
		);
	}

	// Staff on legacy/unknown path → dashboard, not 403
	if (!hasAccess) {
		return <Navigate to={fallbackRoute} replace />;
	}

	// User has access, render children
	return <>{children}</>;
};

export { RoleProtectedRoute };
export default RoleProtectedRoute;
